/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router-dom";
import {
  Storage,
  DimensionScaler as DomainDimensionScaler
} from "@tivocorp/data-domain-lib";

import {
  AuthRejectionArbiter,
  DeviceProvisioningStrategy,
  DomainTokenRefreshStrategy,
  TokenManager
} from "./model/auth";
import { ConnectedInteractor } from "./model/interactor";
import {
  isDeviceIdentityRemovedError,
  persistProvisionData,
  recoverRemovedDeviceIdentityAfterSignIn
} from "./viewModel/signIn/common";
import { Log, Sage, WhinyLogging, ServiceApiLogging } from "./model/logging";
import { DeviceModelSageContextSource } from "./model/logging/DeviceModelSageContextSource";
import { Locale } from "./model/locale";
import { NetworkAvailabilityServiceImpl } from "./model/network";
import { EamModelImpl } from "./model/pushNotification";
import { StorageAdapter } from "./model/storage";
import type { INetworkAvailabilityService } from "./model/interfaces";
import { TsriEndpointResolver } from "./model/tsri";
import {
  UnityAppStore,
  EamBridge,
  setEasRouteNavigator,
  NetworkAvailabilityBridge,
  TsriEndpointRecoveryBridge
} from "./viewModel";
import type { TEasRoute } from "./viewModel";
import { setPartnerLogoUrl } from "./viewModel/branding";
import { notifyBeforeNavigate } from "./viewModel/videoPlayerUi";
import { router } from "./views/App/AppRoutes";
import { DeviceModel } from "./model/device";
import {
  clearAppStorage,
  DimensionScaler,
  installChunkLoadRecovery,
  isFreshInstallLaunch,
  removeFreshInstallFromUrl,
  setAppVersionCookie
} from "./model/utility";
import {
  isDevToolsBuild,
  resolveLaunchAppVersion
} from "./devtools/appVersionOverride";
import {
  FeatureOverrideProvider,
  FeatureStageOverrideProvider
} from "./devtools/featureOverride";
import { FeatureActivation } from "./model/infrastructure";
import { ServiceControlledFeatureState } from "./model/featureActivation";
import { VideoPlayer } from "./views/VideoPlayerUI/VideoPlayer";
import "./index.scss";
import {
  STORAGE_KEY_LANGUAGE,
  STORAGE_KEY_PARTNER_LOGO_URL
} from "./model/storage/storageConfig";
import { DEFAULT_LANGUAGE } from "./config";
import { installMockOverride } from "./devtools/mockOverride";
import { getSupportedLocale } from "./model/locale/localeResolver";

const TAG: string = "index";

async function bootstrap(): Promise<void> {
  Log.i(TAG, "App version: " + DeviceModel.getAppVersionName());

  // Registered before the first dynamic import: a code-split chunk that fails
  // outside a route (a preload, a locale file, an on-demand import inside a
  // component) rejects with nothing rendered to catch it, so it is recovered
  // here. Route-level failures are handled by the root error boundary instead.
  installChunkLoadRecovery();

  // DEBUG-only: route API traffic through a local mock/proxy server when an
  // override is active. No-op in release builds (dead-code-eliminated).
  installMockOverride();

  // DEBUG-only: register the Feature Override and Stage Override providers so
  // developer/QE overrides feed the Feature Activation Framework. In release
  // builds the gate is a constant `false`, so neither provider is created and
  // `FeatureActivation` keeps both null — resolving features by registry rules
  // alone, at their registry stage, whatever localStorage happens to contain.
  if (isDevToolsBuild()) {
    FeatureActivation.getInstance().setOverrideProvider(
      new FeatureOverrideProvider()
    );
    FeatureActivation.getInstance().setStageOverrideProvider(
      new FeatureStageOverrideProvider()
    );
  }

  // The shell app tells us which app version this launch is, via
  // `?x-app-version=v1.0.461` on the iframe URL. Mirror it onto a cookie so
  // it travels with every request from this origin.
  //
  // In debug builds a developer/test override (entered via the 54321 code —
  // see devtools/appVersionOverride.ts) takes precedence over the query param.
  // It has to be applied here, before the first lazy chunk is requested, so
  // every asset this launch fetches resolves against the same build the
  // document did. In release builds the override gate is a constant `false`,
  // so `resolveLaunchAppVersion()` behaves exactly like the bare
  // `getAppVersionFromUrl()` call this line replaced.
  const appVersion: string | null = resolveLaunchAppVersion();
  if (appVersion !== null) {
    setAppVersionCookie(appVersion);
    Log.i(TAG, "x-app-version cookie set to " + appVersion);
  } else {
    Log.w(TAG, "x-app-version query param missing — cookie not set");
  }

  // Shell app reinstall detection: if the shell app flagged this as a
  // fresh install, wipe all iframe-origin storage before anything else.
  if (isFreshInstallLaunch()) {
    Log.i(TAG, "freshInstall=true — clearing all app storage");
    await clearAppStorage();
    removeFreshInstallFromUrl();
  }
  try {
    await StorageAdapter.init();
    Storage.registerStorage("default", StorageAdapter.getInstance());
  } catch (error: unknown) {
    Log.e(TAG, "Storage registration failed", error);
  }

  // Hydrate the last known SERVICE_CONTROLLED feature state from IndexedDB so
  // `isFeatureEnabled()` has a synchronous fallback from the first render.
  try {
    await ServiceControlledFeatureState.getInstance().hydrate();
  } catch (error: unknown) {
    Log.w(TAG, "bootstrap() — failed to hydrate service feature state", error);
  }

  // Restore the persisted Whiny logging window before the first request so
  // the Whiny header is honoured from app launch until the window expires.
  try {
    await WhinyLogging.restoreFromStorage();
  } catch (error: unknown) {
    Log.w(TAG, "bootstrap() — failed to restore Whiny window", error);
  }

  // Restore the persisted Service-API logging window before the first
  // request so cloudcoreTraffic events are honoured from app launch.
  try {
    await ServiceApiLogging.restoreFromStorage();
  } catch (error: unknown) {
    Log.w(
      TAG,
      "bootstrap() — failed to restore Service-API logging window",
      error
    );
  }

  try {
    await DeviceModel.initialize();
  } catch (error: unknown) {
    Log.w(
      TAG,
      "DeviceModel.initialize() failed, using fallbacks: " + String(error)
    );
  }

  // Registered before anything can ask for a token. TokenManager serves every
  // access token in the app and needs a way to mint a new one; without this it
  // can only hand back what storage already holds, and a stale token would
  // fail the first authenticated call instead of quietly renewing.
  // After DeviceModel.initialize() because minting signs with the device key.
  TokenManager.setRefreshStrategy(new DomainTokenRefreshStrategy());

  // A CC API 401/403 is checked against GET /secure before the session is
  // touched; /secure 401 refreshes the token, /secure 403 re-provisions (DPS).
  AuthRejectionArbiter.setSecureProbe(
    (): Promise<void> => new ConnectedInteractor().probeSecureSession()
  );
  AuthRejectionArbiter.setProvisioningStrategy(
    new DeviceProvisioningStrategy(persistProvisionData, {
      matches: isDeviceIdentityRemovedError,
      recover: recoverRemovedDeviceIdentityAfterSignIn
    })
  );

  Sage.init([DeviceModelSageContextSource.fromDeviceModel(DeviceModel)]);
  Sage.logOperations("AppLaunch");

  try {
    const tvProperties = DeviceModel.getTVProperties();
    const detectedLanguage: string = tvProperties.getLang();
    const isLanguageSupported: boolean = tvProperties.hasSupportedLanguage();
    const currentLanguage: string = isLanguageSupported
      ? detectedLanguage
      : DEFAULT_LANGUAGE;
    // The device language is the only input that decides which strings the
    // whole app renders, so an unexpected fallback to English is diagnosable
    // from a launch log alone.
    Log.i(
      TAG,
      `Locale bootstrap: deviceLanguage=${detectedLanguage}, supported=${String(isLanguageSupported)}, locale=${getSupportedLocale(currentLanguage)}`
    );
    await Locale.setLocale(getSupportedLocale(currentLanguage));
    await StorageAdapter.getInstance().set(
      STORAGE_KEY_LANGUAGE,
      currentLanguage,
      Number.POSITIVE_INFINITY
    );
  } catch (error: unknown) {
    Log.e(TAG, "bootstrap()", error);
  }

  // Initialize the network availability service and bridge it to Redux
  const networkService: INetworkAvailabilityService =
    NetworkAvailabilityServiceImpl.getInstance();
  const networkBridge = new NetworkAvailabilityBridge(
    networkService,
    UnityAppStore.dispatch
  );
  networkBridge.start();
  Log.i(TAG, "NetworkAvailabilityService initialized and bridged to Redux");

  // Bridge TSRI endpoint recovery to the alert overlay. Started before the
  // first endpoint lookup so a critical service missing from the very first
  // catalog response still raises the overlay.
  const tsriRecoveryBridge = new TsriEndpointRecoveryBridge(
    TsriEndpointResolver.getInstance(),
    UnityAppStore.dispatch
  );
  tsriRecoveryBridge.start();
  Log.i(TAG, "TsriEndpointResolver bridged to Redux");

  // Bridge EAS alerts to the OSD slice. Started here rather than from a screen
  // so an alert that arrives before any screen has mounted still reaches the
  // display path — EamModelImpl drops an alert outright when no listener is
  // registered.
  const eamBridge = new EamBridge(
    EamModelImpl.getInstance(),
    UnityAppStore.dispatch,
    UnityAppStore.getState,
    UnityAppStore.subscribe
  );
  eamBridge.start();
  Log.i(TAG, "EamModelImpl bridged to Redux");

  // Hands the EAS route restore its way to and from the router. Done here, in
  // the composition root, because `src/viewModel/` must not import
  // `src/views/` and the router is created there — see easRouteRestore.ts.
  setEasRouteNavigator({
    getCurrentRoute: (): TEasRoute => {
      const location = router.state.location;
      return {
        hash: location.hash,
        pathname: location.pathname,
        search: location.search,
        state: location.state
      };
    },
    restoreRoute: (route: TEasRoute): void => {
      // Same hand-off `useNavigationService.navigateTo` makes, so the PiP
      // coordinator gets its chance to act inside this transition.
      notifyBeforeNavigate(route.pathname);
      // `replace`, not push: the detour the alert caused is not somewhere the
      // viewer chose to go, so it must not become a history entry they can
      // navigate back into.
      router
        .navigate(
          {
            hash: route.hash,
            pathname: route.pathname,
            search: route.search
          },
          { replace: true, state: route.state }
        )
        .catch((error: unknown): void => {
          Log.e(TAG, "restoreRoute() — navigation failed", error);
        });
    }
  });

  // Pre-populate Redux with the persisted partner logo URL before React renders
  // so the menu-strip logo is shown immediately on reload without a fallback flash.
  try {
    const storage: StorageAdapter = StorageAdapter.getInstance();
    const persistedLogoUrl: string | null = await storage.get<string>(
      STORAGE_KEY_PARTNER_LOGO_URL
    );
    if (
      persistedLogoUrl !== null &&
      persistedLogoUrl !== undefined &&
      persistedLogoUrl.length > 0
    ) {
      UnityAppStore.dispatch(setPartnerLogoUrl(persistedLogoUrl));
      Log.d(TAG, "bootstrap() — partnerLogoUrl restored from storage");
    }
  } catch (error: unknown) {
    Log.w(TAG, "bootstrap() — failed to restore partnerLogoUrl", error);
  }

  // Initialize DimensionScaler for viewport-relative dimension calculations
  DimensionScaler.initialize(window.innerWidth, window.innerHeight);
  DomainDimensionScaler.initialize(window.innerWidth, window.innerHeight);

  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  );

  root.render(
    <Provider store={UnityAppStore}>
      <VideoPlayer />
      <RouterProvider router={router} />
    </Provider>
  );
}

bootstrap().catch(function (error) {
  Log.e(TAG, "Fatal bootstrap failure", error);
});

// Listen for DEV_SET_COOKIE messages from the shell app to set cookies in the
// hosted web app's origin. Enabled in both debug and release builds via the
// devtools flag; inert when it is off.
if (isDevToolsBuild()) {
  /**
   * Accept DEV_SET_COOKIE only from the shell app that actually hosts this
   * iframe. `event.source` pins the sender to our own parent frame, so a
   * message from any other window or nested frame is dropped; the origin
   * allowlist then covers the two shapes a dev shell takes — a packaged TV
   * build loaded from `file://`, which posts with the opaque origin "null",
   * and a shell served from localhost.
   */
  const isTrustedShellMessage = (event: MessageEvent): boolean => {
    if (window.parent === window.self || event.source !== window.parent) {
      return false;
    }
    if (event.origin === "null" || event.origin === window.location.origin) {
      return true;
    }
    try {
      const hostname: string = new URL(event.origin).hostname;
      return hostname === "localhost" || hostname === "127.0.0.1";
    } catch {
      return false;
    }
  };

  window.addEventListener("message", (event: MessageEvent) => {
    const data = event.data;
    if (data === null || data === undefined) return;
    if (data.type !== "DEV_SET_COOKIE") return;
    // Origin is verified before the cookie is written. The type check above
    // only routes the message, and running it first keeps unrelated traffic
    // (HMR pings, shell bridge responses) out of the warning below.
    if (!isTrustedShellMessage(event)) {
      Log.w(TAG, `DEV_SET_COOKIE rejected — untrusted origin: ${event.origin}`);
      return;
    }
    if (typeof data.name !== "string" || typeof data.value !== "string") return;
    const path = typeof data.path === "string" ? data.path : "/";
    let cookie = `${data.name}=${data.value}; path=${path}`;
    if (typeof data.maxAge === "number") {
      cookie += `; max-age=${data.maxAge}`;
    }
    cookie += "; Secure; SameSite=None";
    document.cookie = cookie;
    Log.d("DEV_SET_COOKIE", `Cookie set: ${data.name}=${data.value}`);
  });
}
